import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '@/services/api';

type Phase = 'verifying' | 'success' | 'error';

/**
 * Convalida email dal link ricevuto via email (/verify-email?token=...).
 * Il token e' monouso (48h): su esito positivo l'utente sa che ora tocca
 * all'admin; su errore puo' farsi reinviare un nuovo link inserendo l'email.
 */
export function VerifyEmailPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [phase, setPhase] = useState<Phase>(token ? 'verifying' : 'error');
  const [error, setError] = useState<string | null>(
    token ? null : 'Link non valido: manca il token di conferma.');
  const [resendEmail, setResendEmail] = useState('');
  const [resendDone, setResendDone] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  // StrictMode in dev monta i componenti due volte: senza guard il secondo
  // POST consumerebbe... no: il token e' gia' consumato dal primo -> errore
  // spurio "link non valido" su una convalida riuscita. Una sola chiamata.
  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    api.post('/auth/verify-email', { token })
      .then(() => setPhase('success'))
      .catch((err: unknown) => {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          ?? 'Conferma non riuscita. Il link potrebbe essere scaduto.';
        setError(msg);
        setPhase('error');
      });
  }, [token]);

  async function handleResend(e: React.FormEvent) {
    e.preventDefault();
    if (!resendEmail) return;
    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification', { email: resendEmail });
      setResendDone(true);
    } catch {
      // 429/rete: messaggio identico (nessun dettaglio utile da dare).
      setResendDone(true);
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-container">
        <div className="login-logo">
          <img src="/icon-128.webp" alt="VVF"
            style={{ width: 72, height: 72, margin: '0 auto 10px', display: 'block' }} />
          <h1>VVF Tracker</h1>
          <p>Conferma email</p>
        </div>

        <div className="login-form-wrap">
          <h2>Conferma email</h2>

          {phase === 'verifying' && (
            <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>
              Verifica in corso…
            </div>
          )}

          {phase === 'success' && (
            <>
              <div style={{ background: '#d4edda', color: '#155724', padding: 14, borderRadius: 8, fontSize: 14, marginBottom: 16 }}>
                ✅ Email confermata! La tua richiesta e' ora <strong>in attesa di
                approvazione</strong> da parte di un amministratore del distaccamento.
                Riceverai un'email appena verra' accettata — non serve fare altro.
              </div>
              <div className="form-footer">
                <Link to="/login">← Torna al login</Link>
              </div>
            </>
          )}

          {phase === 'error' && (
            <>
              <div className="error-message">{error}</div>

              {resendDone ? (
                <div style={{ background: '#d4edda', color: '#155724', padding: 14, borderRadius: 8, fontSize: 14, marginTop: 12 }}>
                  ✉️ Se l'email esiste e non e' ancora confermata, riceverai un
                  nuovo link. Controlla anche lo spam.
                </div>
              ) : (
                <form onSubmit={handleResend} noValidate style={{ marginTop: 12 }}>
                  <div className="form-group">
                    <label htmlFor="resend-email">Reinvia il link di conferma a:</label>
                    <input id="resend-email" type="email" value={resendEmail}
                      placeholder="La tua email di registrazione"
                      onChange={(e) => setResendEmail(e.target.value)} />
                  </div>
                  <button type="submit" className="submit-btn" disabled={resendLoading || !resendEmail}>
                    {resendLoading ? 'Invio…' : 'Reinvia email di conferma'}
                  </button>
                </form>
              )}

              <div className="form-footer">
                <Link to="/login">← Torna al login</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
